/**
 * Redux slice for Copilot state management
 */

/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_VISITED_STEPS } from '../constants/steps';
import type { ChatMessage, CopilotState, ErrorStep } from '../data-layer/types';

import type { GenerateCategoryTemplateResponse } from './copilotApi';

const initialState: CopilotState = {
  navigation: {
    activeStep: 'General',
    completedSteps: [],
    visitedSteps: [...DEFAULT_VISITED_STEPS],
    leftNavLabels: [],
    nameToLabelMap: {},
    mainCategories: [],
    allSteps: [],
    skippedSteps: [],
  },
  messages: [],
  currentStepData: {
    stepName: '',
    fields: [],
    formData: {},
    isLoading: false,
  },
  stepFormData: {
    General: {
      'endpoints.ownerType': ['User'],
      'endpoints.ownerkey': '',
    },
  },
  savedStepData: {},
  revertedSteps: {},
  categoryTemplates: {} as
    | GenerateCategoryTemplateResponse
    | Record<string, never>,
  parsedTemplates: {},
  endpointKey: null,
  appTypeKey: null,
  ui: {
    isLoading: false,
    promptFooterEnabled: false,
    promptFooterMode: 'text' as 'text' | 'upload',
    mode: 'create' as 'create' | 'edit',
    endpointKey: null,
    addAccessInputField: false,
    stepFormStatuses: [],
    fullscreenMode: {
      isActive: false,
      currentStep: '',
      buttonConfig: { kind: 'primary', label: 'Save' },
      filledData: {},
      localFields: [],
    },
    promptFooter: {
      value: '',
      isLoading: false,
      uploadedFile: null,
      fileSizeError: null,
    },
  },
  saveDraftModal: {
    isOpen: false,
    stepName: '',
    targetStep: '',
  },
  entitlementIteration: {
    entitlements: [],
    visibleEntitlements: [0],
    currentIndex: 0,
    showAttributeMapping: false,
  },
  addAccessEntitlementTemplates: [],
  removeAccessEntititlementTemplates: [],
  addAccessPrompts: {} as Record<number, string>, // Store prompts by entitlement index
  removeAccessPrompts: {} as Record<number, string>, // Store prompts by entitlement index
  removeAccessEntitlementIndex: 0,
  connectionPrompt: '', // Single string for connection step
  accountPrompts: {
    CreateAccount: '',
    UpdateAccount: '',
    EnableAccount: '',
    DisableAccount: '',
    RemoveAccount: '',
  } as Record<string, string>,
  errors: {
    createAccount: { hasError: false },
    updateAccount: { hasError: false },
    enableAccount: { hasError: false },
    disableAccount: { hasError: false },
    removeAccount: { hasError: false },
    addAccess: { hasError: false },
    removeAccess: { hasError: false },
    connection: { hasError: false },
    general: { hasError: false },
  },
};

const copilotSlice = createSlice({
  name: 'copilot',
  initialState,
  reducers: {
    // Navigation actions
    setActiveStep: (state, action: PayloadAction<string>) => {
      const stepName = action.payload;

      state.navigation.activeStep = stepName;

      // Add active step to visitedSteps if not already present
      if (!state.navigation.visitedSteps.includes(stepName)) {
        state.navigation.visitedSteps.push(stepName);
      }

      // Load step-specific form data into currentStepData
      state.currentStepData.stepName = stepName;
      state.currentStepData.formData = state.stepFormData[stepName] || {};
    },
    addCompletedStep: (state, action: PayloadAction<string>) => {
      if (!state.navigation.completedSteps.includes(action.payload)) {
        state.navigation.completedSteps.push(action.payload);
      }

      // Remove from skipped steps when marking as completed
      state.navigation.skippedSteps = state.navigation.skippedSteps.filter(
        (step) => step !== action.payload
      );
    },
    addVisitedStep: (state, action: PayloadAction<string>) => {
      if (!state.navigation.visitedSteps.includes(action.payload)) {
        state.navigation.visitedSteps.push(action.payload);
      }
    },
    addSkippedStep: (state, action: PayloadAction<string>) => {
      if (!state.navigation.skippedSteps.includes(action.payload)) {
        state.navigation.skippedSteps.push(action.payload);
      }
    },
    removeSkippedStep: (state, action: PayloadAction<string>) => {
      state.navigation.skippedSteps = state.navigation.skippedSteps.filter(
        (step) => step !== action.payload
      );
    },
    setNavigationSteps: (
      state,
      action: PayloadAction<{
        leftNavLabels: string[];
        nameToLabelMap: Record<string, string>;
        mainCategories: string[];
        allSteps: string[];
      }>
    ) => {
      state.navigation.leftNavLabels = action.payload.leftNavLabels;
      state.navigation.nameToLabelMap = action.payload.nameToLabelMap;
      state.navigation.mainCategories = action.payload.mainCategories;
      state.navigation.allSteps = action.payload.allSteps;
    },
    updateNameToLabelMap: (
      state,
      action: PayloadAction<{ stepName: string; label: string }>
    ) => {
      state.navigation.nameToLabelMap[action.payload.stepName] =
        action.payload.label;

      if (!state.navigation.leftNavLabels.includes(action.payload.stepName)) {
        state.navigation.leftNavLabels.push(action.payload.stepName);
      }
    },
    setCategoryTemplates: (
      state,
      action: PayloadAction<GenerateCategoryTemplateResponse>
    ) => {
      state.categoryTemplates = action.payload;

      // Set appTypeKey from the API response
      if (action.payload.configuration.appTypeKey) {
        state.appTypeKey = action.payload.configuration.appTypeKey;
      }
    },
    setParsedTemplates: (state, action: PayloadAction<Record<string, any>>) => {
      state.parsedTemplates = action.payload;
    },
    setEndpointKey: (state, action: PayloadAction<number>) => {
      state.endpointKey = action.payload;
    },
    setAppTypeKey: (state, action: PayloadAction<number>) => {
      state.appTypeKey = action.payload;
    },

    // Message actions
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      const activeStep = state.navigation.activeStep ?? '';

      // Construct ID with activeStep prefix if not already prefixed
      const originalId = action.payload.id;
      const messageId = originalId.startsWith(`${activeStep}-`)
        ? originalId
        : `${activeStep}-${originalId}`;

      // Determine entitlement indices based on active step
      let addAccessEntitlementIndex = -1;
      let removeAccessEntitlementIndex = -1;

      if (activeStep && activeStep.includes('AddAccess_')) {
        addAccessEntitlementIndex = parseInt(activeStep.split('_')[1], 10) || 0;
      } else if (activeStep === 'AddAccess') {
        addAccessEntitlementIndex =
          state.entitlementIteration.currentIndex || 0;
      } else if (activeStep === 'RemoveAccess') {
        removeAccessEntitlementIndex = state.removeAccessEntitlementIndex || 0;
      }

      // Create message with step-based ID, stepName, and entitlement indices
      const messageWithStepInfo = {
        ...action.payload,
        id: messageId,
        stepName: activeStep,
        addAccessEntitlementIndex,
        removeAccessEntitlementIndex,
      };

      // Prevent duplicate messages with same ID
      const existingMessage = state.messages.find(
        (msg) => msg.id === messageWithStepInfo.id
      );

      if (!existingMessage) {
        // For entitlement messages, group by entitlement index
        if (
          addAccessEntitlementIndex >= 0 ||
          removeAccessEntitlementIndex >= 0
        ) {
          // Find the last message for this specific entitlement index
          let insertIndex = -1;

          for (let i = state.messages.length - 1; i >= 0; i -= 1) {
            const msg = state.messages[i];
            const msgEntitlementIndex =
              addAccessEntitlementIndex >= 0
                ? msg.addAccessEntitlementIndex
                : msg.removeAccessEntitlementIndex;

            if (
              msgEntitlementIndex ===
              (addAccessEntitlementIndex >= 0
                ? addAccessEntitlementIndex
                : removeAccessEntitlementIndex)
            ) {
              insertIndex = i + 1;
              break;
            }
          }

          if (insertIndex !== -1) {
            state.messages.splice(insertIndex, 0, messageWithStepInfo);
          } else {
            state.messages.push(messageWithStepInfo);
          }
        } else {
          // Find the last message for the current active step
          let lastStepMessageIndex = -1;

          // eslint-disable-next-line no-plusplus
          for (let i = state.messages.length - 1; i >= 0; i -= 1) {
            if (state.messages[i].stepName === activeStep) {
              lastStepMessageIndex = i;
              break;
            }
          }

          const insertIndex = lastStepMessageIndex + 1;

          // Insert after last message of current step, or append to end
          if (lastStepMessageIndex !== -1) {
            state.messages.splice(insertIndex, 0, messageWithStepInfo);
          } else {
            state.messages.push(messageWithStepInfo);
          }
        }
      }
    },
    updateMessage: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<ChatMessage> }>
    ) => {
      const messageIndex = state.messages.findIndex(
        (msg) => msg.id === action.payload.id
      );

      if (messageIndex !== -1) {
        Object.assign(state.messages[messageIndex], action.payload.updates);
      }
    },
    removeMessage: (state, action: PayloadAction<string>) => {
      const messageIndex = state.messages.findIndex(
        (msg) => msg.id === action.payload
      );

      if (messageIndex !== -1) {
        state.messages.splice(messageIndex, 1);
      }
    },
    removeMessagesBySubType: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter((message) => {
        if (message.type === 'ai_response') {
          const aiMessage = message as { subType: string };

          return aiMessage.subType !== action.payload;
        }

        if (message.type === 'step_form') {
          const stepFormMessage = message as { subType: string };

          return stepFormMessage.subType !== action.payload;
        }

        if (message.type === 'user_response') {
          const userMessage = message as { subType: string };

          return userMessage.subType !== action.payload;
        }

        if (message.type === 'file_prompt') {
          const fileMessage = message as { subType: string };

          return fileMessage.subType !== action.payload;
        }

        return true;
      });
    },
    removeMessagesBetweenSteps: (
      state,
      action: PayloadAction<{ currentStepName: string }>
    ) => {
      const { currentStepName } = action.payload;

      // Find the index of the current step form
      const currentStepIndex = state.messages.findIndex(
        (msg) => msg.type === 'step_form' && msg.stepName === currentStepName
      );

      if (currentStepIndex === -1) {
        return;
      } // Step not found

      // Find the index of the next step form after current step
      const nextStepIndex = state.messages.findIndex(
        (msg, index) => index > currentStepIndex && msg.type === 'step_form'
      );

      // If no next step found, remove from current step to end
      const endIndex =
        nextStepIndex === -1 ? state.messages.length : nextStepIndex;

      // Remove messages between current step (exclusive) and next step (exclusive)
      state.messages.splice(
        currentStepIndex + 1,
        endIndex - currentStepIndex - 1
      );
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    removeMessagesBetweenEntitlements: (
      state,
      action: PayloadAction<{
        currentStepName: string;
        nextStepName?: string;
      }>
    ) => {
      const { currentStepName, nextStepName } = action.payload;

      // Find the index of the current entitlement form
      // Look for step_form messages that match the entitlement pattern
      const currentStepIndex = state.messages.findIndex((msg) => {
        if (msg.type !== 'step_form') {
          return false;
        }

        // Handle both old pattern (stepName: 'AddAccess' with entitlementIndex in formData)
        // and new pattern (stepName: 'AddAccess_0')
        if (currentStepName.includes('AddAccess_')) {
          const entitlementIndex = parseInt(currentStepName.split('_')[1], 10);

          return (
            msg.stepName === 'AddAccess' &&
            msg.formData.entitlementIndex === entitlementIndex
          );
        }

        return msg.stepName === currentStepName;
      });

      if (currentStepIndex === -1) {
        return;
      }

      // Find the index of the next entitlement form
      let nextStepIndex = -1;

      if (nextStepName) {
        nextStepIndex = state.messages.findIndex((msg, index) => {
          if (index <= currentStepIndex || msg.type !== 'step_form') {
            return false;
          }

          if (nextStepName.includes('AddAccess_')) {
            const entitlementIndex = parseInt(nextStepName.split('_')[1], 10);

            return (
              msg.stepName === 'AddAccess' &&
              msg.formData.entitlementIndex === entitlementIndex
            );
          }

          return msg.stepName === nextStepName;
        });
      }

      // If no next step found, remove from current step to end
      const endIndex =
        nextStepIndex === -1 ? state.messages.length : nextStepIndex;

      // Remove messages between current step (exclusive) and next step (exclusive)
      state.messages.splice(
        currentStepIndex + 1,
        endIndex - currentStepIndex - 1
      );
    },
    addMessageAfterEntitlement: (
      state,
      action: PayloadAction<{
        stepName: string;
        message: ChatMessage;
      }>
    ) => {
      const { stepName, message } = action.payload;

      // Find the index of the entitlement form
      let stepIndex = -1;

      // Handle AddAccess_X pattern
      if (stepName.includes('AddAccess_')) {
        const entitlementIndex = parseInt(stepName.split('_')[1], 10);

        stepIndex = state.messages.findIndex(
          (msg) =>
            msg.type === 'step_form' &&
            msg.stepName === 'AddAccess' &&
            msg.formData.entitlementIndex === entitlementIndex
        );
      } else {
        stepIndex = state.messages.findIndex(
          (msg) => msg.type === 'step_form' && msg.stepName === stepName
        );
      }

      if (stepIndex !== -1) {
        // Insert message after the entitlement form
        state.messages.splice(stepIndex + 1, 0, message);
      } else {
        // If step not found, add to end
        state.messages.push(message);
      }
    },
    insertMessageAtIndex: (
      state,
      action: PayloadAction<{
        index: number;
        message: ChatMessage;
      }>
    ) => {
      const { index, message } = action.payload;

      state.messages.splice(index, 0, message);
    },
    insertAfterEntitlement: (
      state,
      action: PayloadAction<{
        entitlementIndex: number;
        message: ChatMessage;
      }>
    ) => {
      const { entitlementIndex, message } = action.payload;

      // Find the entitlement form in current state
      const entitlementFormIndex = state.messages.findIndex(
        (msg) =>
          msg.type === 'step_form' &&
          msg.subType === 'accessAttributeMapping' &&
          msg.formData.entitlementIndex === entitlementIndex
      );

      if (entitlementFormIndex !== -1) {
        // Insert message right after the entitlement form
        state.messages.splice(entitlementFormIndex + 1, 0, message);
      } else {
        // Fallback: add to end
        state.messages.push(message);
      }
    },
    addMessageAfterEntitlementByIndex: (
      state,
      action: PayloadAction<{
        entitlementIndex: number;
        message: ChatMessage;
        isAddAccess?: boolean;
      }>
    ) => {
      const { entitlementIndex, message, isAddAccess = true } = action.payload;

      // Find the last message for this specific entitlement index
      let insertIndex = -1;

      for (let i = state.messages.length - 1; i >= 0; i -= 1) {
        const msg = state.messages[i];
        const msgEntitlementIndex = isAddAccess
          ? msg.addAccessEntitlementIndex
          : msg.removeAccessEntitlementIndex;

        if (msgEntitlementIndex === entitlementIndex) {
          insertIndex = i + 1;
          break;
        }
      }

      // Set the appropriate entitlement index on the message
      const messageWithIndex = {
        ...message,
        addAccessEntitlementIndex: isAddAccess ? entitlementIndex : -1,
        removeAccessEntitlementIndex: isAddAccess ? -1 : entitlementIndex,
      };

      if (insertIndex !== -1) {
        // Insert after the last message of this entitlement
        state.messages.splice(insertIndex, 0, messageWithIndex);
      } else {
        // Fallback: add to end
        state.messages.push(messageWithIndex);
      }
    },

    // Entitlement message management
    addMessageToEntitlement: (
      state,
      action: PayloadAction<{
        entitlementIndex: number;
        entitlementType: 'addAccess' | 'removeAccess';
        message: ChatMessage;
      }>
    ) => {
      const { entitlementIndex, entitlementType, message } = action.payload;

      // Set parent relationship on the message
      const messageWithParent = {
        ...message,
        parentEntitlementIndex: entitlementIndex,
        entitlementType,
        addAccessEntitlementIndex:
          entitlementType === 'addAccess' ? entitlementIndex : -1,
        removeAccessEntitlementIndex:
          entitlementType === 'removeAccess' ? entitlementIndex : -1,
      };

      // Find the last message for this specific entitlement
      let insertIndex = -1;

      for (let i = state.messages.length - 1; i >= 0; i -= 1) {
        const msg = state.messages[i];

        if (
          msg.parentEntitlementIndex === entitlementIndex &&
          msg.entitlementType === entitlementType
        ) {
          insertIndex = i + 1;
          break;
        }
      }

      if (insertIndex !== -1) {
        // Insert after the last message of this entitlement
        state.messages.splice(insertIndex, 0, messageWithParent);
      } else {
        // First message for this entitlement, add to end
        state.messages.push(messageWithParent);
      }
    },
    clearEntitlementMessages: (
      state,
      action: PayloadAction<{
        entitlementIndex: number;
        entitlementType: 'addAccess' | 'removeAccess';
        keepForm?: boolean;
      }>
    ) => {
      const {
        entitlementIndex,
        entitlementType,
        keepForm = true,
      } = action.payload;

      // Remove all messages for this entitlement except the form itself
      state.messages = state.messages.filter((msg) => {
        const belongsToEntitlement =
          msg.parentEntitlementIndex === entitlementIndex &&
          msg.entitlementType === entitlementType;

        if (!belongsToEntitlement) {
          return true;
        }

        // Keep the form if requested
        if (keepForm && msg.type === 'step_form') {
          return true;
        }

        return false;
      });
    },

    // Clear redundant template storage
    clearRedundantTemplates: (state) => {
      // Remove AddAccessTemplate_X entries as they duplicate stepFormData
      Object.keys(state.stepFormData).forEach((key) => {
        if (
          key.startsWith('AddAccessTemplate_') ||
          key.startsWith('RemoveAccessTemplate_')
        ) {
          delete state.stepFormData[key];
        }
      });
    },

    // Ensure consistent entitlement index tracking
    syncEntitlementIndex: (state) => {
      const { activeStep } = state.navigation;

      if (activeStep && activeStep.includes('AddAccess_')) {
        const index = parseInt(activeStep.split('_')[1], 10);

        if (!Number.isNaN(index)) {
          state.entitlementIteration.currentIndex = index;
        }
      }
    },
    updateStepFormData: (
      state,
      action: PayloadAction<{ stepName: string; formData: Record<string, any> }>
    ) => {
      const { stepName, formData } = action.payload;

      // Store in step-specific storage
      state.stepFormData[stepName] = {
        ...state.stepFormData[stepName],
        ...formData,
      };

      // Update current step data if it's the active step
      if (state.navigation.activeStep === stepName) {
        state.currentStepData.stepName = stepName;
        Object.assign(state.currentStepData.formData, formData);
      }
    },
    setSavedStepData: (
      state,
      action: PayloadAction<{ stepName: string; formData: Record<string, any> }>
    ) => {
      const { stepName, formData } = action.payload;

      state.savedStepData[stepName] = {
        ...state.savedStepData[stepName],
        ...formData,
      };
    },
    setStepLoading: (state, action: PayloadAction<boolean>) => {
      state.currentStepData.isLoading = action.payload;
    },
    setCurrentStepData: (
      state,
      action: PayloadAction<{
        stepName: string;
        fields: any[];
        formData: Record<string, any>;
      }>
    ) => {
      state.currentStepData = {
        ...action.payload,
        isLoading: false,
      };
    },

    // UI actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.ui.isLoading = action.payload;
    },
    setPromptFooterEnabled: (state, action: PayloadAction<boolean>) => {
      state.ui.promptFooterEnabled = action.payload;
    },
    setPromptFooterMode: (state, action: PayloadAction<'text' | 'upload'>) => {
      state.ui.promptFooterMode = action.payload;
    },
    setAddAccessInputField: (state, action: PayloadAction<boolean>) => {
      state.ui.addAccessInputField = action.payload;
    },
    setPromptFooterState: (
      state,
      action: PayloadAction<{
        value?: string;
        isLoading?: boolean;
        uploadedFile?: File | null;
        fileSizeError?: string | null;
      }>
    ) => {
      Object.assign(state.ui.promptFooter, action.payload);
    },
    resetPromptFooterState: (state) => {
      state.ui.promptFooter = {
        value: '',
        isLoading: false,
        uploadedFile: null,
        fileSizeError: null,
      };
      state.ui.promptFooterEnabled = false;
      state.ui.promptFooterMode = 'text';
    },
    openSaveDraftModal: (
      state,
      action: PayloadAction<{ stepName: string; targetStep: string }>
    ) => {
      state.saveDraftModal = {
        isOpen: true,
        stepName: action.payload.stepName,
        targetStep: action.payload.targetStep,
      };
    },
    closeSaveDraftModal: (state) => {
      state.saveDraftModal = {
        isOpen: false,
        stepName: '',
        targetStep: '',
      };
    },
    revertStepFormData: (state, action: PayloadAction<string>) => {
      const stepName = action.payload;
      const savedData = state.savedStepData[stepName];

      if (savedData) {
        state.stepFormData[stepName] = { ...savedData };

        // Set timestamp to force re-render only for this step
        if (!state.revertedSteps) {
          state.revertedSteps = {};
        }

        state.revertedSteps[stepName] = Date.now();
      }
    },
    setMode: (
      state,
      action: PayloadAction<{
        mode: 'create' | 'edit';
        endpointKey: string | null;
      }>
    ) => {
      state.ui.mode = action.payload.mode;
      state.ui.endpointKey = action.payload.endpointKey;

      // Clear active step in edit mode only if no active step is currently set
      if (action.payload.mode === 'edit' && !state.navigation.activeStep) {
        state.navigation.activeStep = '';
      }
    },

    setFullscreenMode: (
      state,
      action: PayloadAction<{
        isActive: boolean;
        currentStep: string;
        buttonConfig: { kind: string; label: string };
        filledData?: Record<string, any>;
        localFields?: any[];
        entitlementIndex?: number;
      }>
    ) => {
      state.ui.fullscreenMode = action.payload;
    },

    // Entitlement iteration actions
    setEntitlementSteps: (
      state,
      action: PayloadAction<{ id: number; value: string }[]>
    ) => {
      state.entitlementIteration.entitlements = action.payload;
      state.entitlementIteration.currentIndex = 0;
      state.entitlementIteration.visibleEntitlements = [0];
      state.entitlementIteration.showAttributeMapping = true;
    },
    updateEntitlements: (
      state,
      action: PayloadAction<{ id: number; value: string }[]>
    ) => {
      state.entitlementIteration.entitlements = action.payload;
    },
    incrementEntitlementIndex: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const nextIndex = state.entitlementIteration.currentIndex + 1;

      if (nextIndex < state.entitlementIteration.entitlements.length) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        state.entitlementIteration.currentIndex = nextIndex;

        if (
          !state.entitlementIteration.visibleEntitlements.includes(nextIndex)
        ) {
          state.entitlementIteration.visibleEntitlements.push(nextIndex);
        }
      }
    },
    hideAttributeMapping: (state) => {
      state.entitlementIteration.showAttributeMapping = false;
    },
    addVisibleEntitlement: (state, action: PayloadAction<number>) => {
      const index = action.payload;

      if (!state.entitlementIteration.visibleEntitlements.includes(index)) {
        state.entitlementIteration.visibleEntitlements.push(index);
      }
    },
    setCurrentEntitlementIndex: (state, action: PayloadAction<number>) => {
      state.entitlementIteration.currentIndex = action.payload;
    },
    resetEntitlementIteration: (state) => {
      state.entitlementIteration = {
        entitlements: [],
        visibleEntitlements: [0],
        currentIndex: 0,
        showAttributeMapping: false,
      };
      // Reset prompts when resetting entitlement iteration
      state.addAccessPrompts = {};
      state.removeAccessPrompts = {};
    },
    setAddAccessPrompt: (
      state,
      action: PayloadAction<{ index: number; prompt: string }>
    ) => {
      const { index, prompt } = action.payload;

      state.addAccessPrompts[index] = prompt;
    },
    setRemoveAccessPrompt: (
      state,
      action: PayloadAction<{ index: number; prompt: string }>
    ) => {
      const { index, prompt } = action.payload;

      state.removeAccessPrompts[index] = prompt;
    },
    setRemoveAccessEntitlementIndex: (state, action: PayloadAction<number>) => {
      state.removeAccessEntitlementIndex = action.payload;
    },
    resetRemoveAccessEntitlementIndex: (state) => {
      state.removeAccessEntitlementIndex = 0;
    },

    setAddAccessEntitlementTemplates: (
      state,
      action: PayloadAction<unknown[]>
    ) => {
      state.addAccessEntitlementTemplates = action.payload;
    },

    removeAddAccessEntitlementTemplate: (
      state,
      action: PayloadAction<number>
    ) => {
      state.addAccessEntitlementTemplates =
        state.addAccessEntitlementTemplates.filter(
          (_, index) => index !== action.payload
        );
    },

    updateAddAccessEntitlementTemplate: (
      state,
      action: PayloadAction<{ index: number; template: unknown }>
    ) => {
      const { index, template } = action.payload;

      if (index < state.addAccessEntitlementTemplates.length) {
        state.addAccessEntitlementTemplates[index] = template;
      }
    },

    addTransformedTemplate: (state, action: PayloadAction<unknown>) => {
      state.addAccessEntitlementTemplates = [
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ...state.addAccessEntitlementTemplates,
        action.payload,
      ];
    },

    addRemoveAccessEntititlementTemplates: (
      state,
      action: PayloadAction<unknown>
    ) => {
      state.removeAccessEntititlementTemplates = [
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ...state.removeAccessEntititlementTemplates,
        action.payload,
      ];
    },

    setFormStatus: (
      state,
      action: PayloadAction<{
        stepName: string;
        formStatus: 'expandableview' | 'collapsedview' | 'expandedview';
      }>
    ) => {
      const { stepName, formStatus } = action.payload;

      // If expanding a form, collapse all others
      if (formStatus === 'expandableview' || formStatus === 'expandedview') {
        state.ui.stepFormStatuses.forEach((step) => {
          if (step.stepName !== stepName) {
            step.formStatus = 'collapsedview';
          }
        });
      }

      const existingIndex = state.ui.stepFormStatuses.findIndex(
        (step) => step.stepName === stepName
      );

      if (existingIndex >= 0) {
        state.ui.stepFormStatuses[existingIndex].formStatus = formStatus;
      } else {
        state.ui.stepFormStatuses.push({ stepName, formStatus });
      }
    },

    // Prompt storage actions
    setConnectionPrompt: (state, action: PayloadAction<string>) => {
      state.connectionPrompt = action.payload;
    },
    setAccountPrompt: (
      state,
      action: PayloadAction<{ stepName: string; prompt: string }>
    ) => {
      const { stepName, prompt } = action.payload;

      state.accountPrompts[stepName] = prompt;
    },

    resetCopilotState: () => initialState,

    initializeOwnerKey: (
      state,
      action: PayloadAction<{ userId: string; username: string }>
    ) => {
      const { userId, username } = action.payload;
      const ownerType = state.stepFormData.General['endpoints.ownerType'];
      const isUserSelected =
        Array.isArray(ownerType) && ownerType.includes('User');

      // Only set default value if User is selected and no value exists
      if (isUserSelected && !state.stepFormData.General['endpoints.ownerkey']) {
        state.stepFormData.General['endpoints.ownerkey'] = JSON.stringify({
          id: userId,
          value: username,
        });
      }
    },

    handleOwnerTypeChange: (
      state,
      action: PayloadAction<{
        ownerType: string[];
        userId?: string;
        username?: string;
      }>
    ) => {
      const { ownerType, userId, username } = action.payload;
      const isUserSelected = ownerType.includes('User');
      const isUserGroupSelected =
        ownerType.includes('UserGroup') || ownerType.includes('User Group');

      // Update owner type
      state.stepFormData.General['endpoints.ownerType'] = ownerType;

      if (isUserSelected) {
        // Set default user value if switching to User
        if (userId && username) {
          state.stepFormData.General['endpoints.ownerkey'] = JSON.stringify({
            id: userId,
            value: username,
          });
        }
      } else if (isUserGroupSelected) {
        // Clear owner key when switching to UserGroup
        state.stepFormData.General['endpoints.ownerkey'] = '';
      }
    },
    // Error management actions
    setError: (
      state,
      action: PayloadAction<{
        step: ErrorStep;
        hasError: boolean;
        message?: string;
      }>
    ) => {
      const { step, hasError, message } = action.payload;

      state.errors[step] = { hasError, message };
    },
    clearError: (state, action: PayloadAction<ErrorStep>) => {
      const step = action.payload;

      state.errors[step] = { hasError: false };
    },
    clearAllErrors: (state) => {
      (Object.keys(state.errors) as ErrorStep[]).forEach((key) => {
        state.errors[key] = { hasError: false };
      });
    },
  },
});

export const {
  setActiveStep,
  addCompletedStep,
  addVisitedStep,
  setNavigationSteps,
  updateNameToLabelMap,
  setCategoryTemplates,
  setParsedTemplates,
  setEndpointKey,
  setAppTypeKey,
  addMessage,
  insertMessageAtIndex,
  insertAfterEntitlement,
  addMessageAfterEntitlementByIndex,
  addMessageToEntitlement,
  clearEntitlementMessages,
  updateMessage,
  removeMessage,
  removeMessagesBySubType,
  removeMessagesBetweenSteps,
  removeMessagesBetweenEntitlements,
  addMessageAfterEntitlement,
  clearMessages,
  updateStepFormData,
  setSavedStepData,
  setStepLoading,
  setCurrentStepData,
  setLoading,
  setPromptFooterEnabled,
  setPromptFooterMode,
  setAddAccessInputField,
  setPromptFooterState,
  resetPromptFooterState,
  openSaveDraftModal,
  closeSaveDraftModal,
  revertStepFormData,
  setMode,
  setFullscreenMode,
  setEntitlementSteps,
  updateEntitlements,
  incrementEntitlementIndex,
  hideAttributeMapping,
  addVisibleEntitlement,
  setCurrentEntitlementIndex,
  resetEntitlementIteration,
  setAddAccessPrompt,
  setRemoveAccessPrompt,
  setRemoveAccessEntitlementIndex,
  resetRemoveAccessEntitlementIndex,
  setAddAccessEntitlementTemplates,
  removeAddAccessEntitlementTemplate,
  updateAddAccessEntitlementTemplate,
  addTransformedTemplate,
  addRemoveAccessEntititlementTemplates,
  setFormStatus,
  initializeOwnerKey,
  handleOwnerTypeChange,
  setConnectionPrompt,
  setAccountPrompt,
  resetCopilotState,
  addSkippedStep,
  removeSkippedStep,
  setError,
  clearError,
  clearAllErrors,
  clearRedundantTemplates,
  syncEntitlementIndex,
} = copilotSlice.actions;

export default copilotSlice.reducer;
